/**
 * LocalStorageRepository - localStorage implementation for web
 *
 * Persiste su localStorage del browser
 * Features:
 * - Persistenza tra sessioni
 * - Serializzazione automatica JSON
 * - Quota management
 * - Fallback a MemoryRepository se quota piena
 */

import type {
  IDataRepository,
  QuerySpec,
  PatchOperation,
  BatchOperation,
  BatchResult,
  RepositoryConfig,
  RepositoryMetadata,
} from './types';
import { MemoryRepository } from './MemoryRepository';

export class LocalStorageRepository implements IDataRepository {
  private storageKey: string;
  private memoryFallback: MemoryRepository;
  private isMemoryMode = false;

  constructor(storageKey = 'business-assistant-db') {
    this.storageKey = storageKey;
    this.memoryFallback = new MemoryRepository();
  }

  async init(config?: RepositoryConfig): Promise<void> {
    try {
      // Test localStorage availability
      const testKey = `${this.storageKey}:test`;
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);

      // Load existing data into memory fallback for faster queries
      await this.loadToMemory();
    } catch (error) {
      console.warn('localStorage not available, falling back to MemoryRepository');
      this.isMemoryMode = true;
      await this.memoryFallback.init(config);
    }
  }

  async get<T = any>(id: string): Promise<T | null> {
    if (this.isMemoryMode) {
      return this.memoryFallback.get(id);
    }

    try {
      const key = this.makeKey(id);
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(`Error reading from localStorage (key: ${id}):`, error);
      return null;
    }
  }

  async query<T = any>(spec: QuerySpec): Promise<T[]> {
    // Use memory fallback for queries (faster)
    return this.memoryFallback.query(spec);
  }

  async put<T = any>(record: T): Promise<T> {
    const id = (record as any).id;
    if (!id) {
      throw new Error('Record must have an id field');
    }

    // Add metadata
    const now = new Date().toISOString();
    const enrichedRecord = {
      ...record,
      updatedAt: now,
      createdAt: (record as any).createdAt || now,
    };

    if (this.isMemoryMode) {
      return this.memoryFallback.put(enrichedRecord);
    }

    try {
      // Save to localStorage
      const key = this.makeKey(id);
      const data = JSON.stringify(enrichedRecord);
      localStorage.setItem(key, data);

      // Update memory cache
      await this.memoryFallback.put(enrichedRecord);

      // Update index
      await this.updateIndex(id);

      return enrichedRecord;
    } catch (error) {
      if (this.isQuotaExceeded(error)) {
        console.warn('localStorage quota exceeded, switching to memory mode');
        this.isMemoryMode = true;
        return this.memoryFallback.put(enrichedRecord);
      }
      throw error;
    }
  }

  async patch<T = any>(id: string, ops: PatchOperation[]): Promise<T> {
    if (this.isMemoryMode) {
      return this.memoryFallback.patch(id, ops);
    }

    const record = await this.get(id);
    if (!record) {
      throw new Error(`Record with id ${id} not found`);
    }

    // Apply patches via memory fallback
    const updated = await this.memoryFallback.patch(id, ops);

    // Save back to localStorage
    await this.put(updated);

    return updated;
  }

  async delete(id: string): Promise<void> {
    if (this.isMemoryMode) {
      return this.memoryFallback.delete(id);
    }

    try {
      const key = this.makeKey(id);
      localStorage.removeItem(key);
      await this.memoryFallback.delete(id);
      await this.removeFromIndex(id);
    } catch (error) {
      console.error(`Error deleting from localStorage (id: ${id}):`, error);
    }
  }

  async batch(ops: BatchOperation[]): Promise<BatchResult> {
    const results: BatchResult['results'] = [];

    for (const op of ops) {
      try {
        switch (op.type) {
          case 'put':
            await this.put(op.record);
            results.push({ id: op.record.id, success: true });
            break;

          case 'patch':
            await this.patch(op.id, op.ops);
            results.push({ id: op.id, success: true });
            break;

          case 'delete':
            await this.delete(op.id);
            results.push({ id: op.id, success: true });
            break;
        }
      } catch (error) {
        results.push({
          id: op.type === 'delete' ? op.id : op.type === 'patch' ? op.id : op.record.id,
          success: false,
          error: (error as Error).message,
        });
      }
    }

    const success = results.every((r) => r.success);
    return { success, results };
  }

  async count(spec: QuerySpec): Promise<number> {
    return this.memoryFallback.count(spec);
  }

  async exists(id: string): Promise<boolean> {
    if (this.isMemoryMode) {
      return this.memoryFallback.exists(id);
    }

    const key = this.makeKey(id);
    return localStorage.getItem(key) !== null;
  }

  /**
   * Get repository metadata
   */
  getMetadata(): RepositoryMetadata {
    const storageUsed = this.calculateStorageUsed();
    const storageQuota = this.getStorageQuota();

    return {
      name: 'LocalStorageRepository',
      version: '1.0.0',
      capabilities: {
        transactions: false,
        fullTextSearch: true,
        vectorSearch: false,
        offline: true,
        sync: false,
      },
      storage: {
        used: storageUsed,
        available: storageQuota - storageUsed,
        quota: storageQuota,
      },
    };
  }

  /**
   * Clear all data
   */
  async clear(): Promise<void> {
    if (this.isMemoryMode) {
      this.memoryFallback.clear();
      return;
    }

    const index = await this.getIndex();
    for (const id of index) {
      const key = this.makeKey(id);
      localStorage.removeItem(key);
    }

    localStorage.removeItem(this.makeIndexKey());
    this.memoryFallback.clear();
  }

  // =============================================================================
  // PRIVATE HELPER METHODS
  // =============================================================================

  private makeKey(id: string): string {
    return `${this.storageKey}:record:${id}`;
  }

  private makeIndexKey(): string {
    return `${this.storageKey}:index`;
  }

  private async getIndex(): Promise<string[]> {
    try {
      const data = localStorage.getItem(this.makeIndexKey());
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  private async updateIndex(id: string): Promise<void> {
    const index = await this.getIndex();
    if (!index.includes(id)) {
      index.push(id);
      localStorage.setItem(this.makeIndexKey(), JSON.stringify(index));
    }
  }

  private async removeFromIndex(id: string): Promise<void> {
    const index = await this.getIndex();
    const newIndex = index.filter((i) => i !== id);
    localStorage.setItem(this.makeIndexKey(), JSON.stringify(newIndex));
  }

  private async loadToMemory(): Promise<void> {
    const index = await this.getIndex();
    for (const id of index) {
      const record = await this.get(id);
      if (record) {
        await this.memoryFallback.put(record);
      }
    }
  }

  private isQuotaExceeded(error: any): boolean {
    return (
      error instanceof DOMException &&
      (error.code === 22 ||
        error.code === 1014 ||
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
    );
  }

  private calculateStorageUsed(): number {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.storageKey)) {
        const value = localStorage.getItem(key);
        total += key.length + (value?.length || 0);
      }
    }
    return total;
  }

  private getStorageQuota(): number {
    // Most browsers have 5-10MB localStorage quota
    // Return conservative estimate
    return 5 * 1024 * 1024; // 5MB
  }
}
